/**
 * Pure math for the Price Insights Price Range Bar (Dealscore).
 *
 * The bar always shows three equally-sized segments (Low / Fair / High) —
 * visual thirds regardless of the actual percentile spread. The hotel
 * price indicator is mapped non-linearly: within each band, position is
 * proportional to where the price sits between the band's edges.
 *
 * Percentiles come from the API on the latest
 * `similar_hotels_history[].detailed_price` entry. `p35` is the Low/Fair
 * boundary and `p65` is the Fair/High boundary. When the hotel price
 * falls outside the Fair band it anchors the opposing outer bound so the
 * indicator is never clipped off the bar.
 */

export interface PriceRangeBarData {
  p10: number
  p35: number
  p65: number
  p90: number
  hotelPrice: number
}

export interface PriceRangeBarBounds {
  leftBound: number
  rightBound: number
}

// Each band occupies exactly one third of the bar. Keeping these as named
// constants makes the band-mapping math easier to read.
const BAND_START_LOW = 0
const BAND_START_FAIR = 100 / 3
const BAND_START_HIGH = 200 / 3
const BAND_WIDTH = 100 / 3

const isNumFinite = Number.isFinite

export const isValidPriceRangeData = (
  data: PriceRangeBarData | null | undefined
): data is PriceRangeBarData => {
  if (!data) return false
  const {p10, p35, p65, p90, hotelPrice} = data
  return (
    isNumFinite(p10) &&
    isNumFinite(p35) &&
    isNumFinite(p65) &&
    isNumFinite(p90) &&
    isNumFinite(hotelPrice) &&
    p10 <= p35 &&
    p35 <= p65 &&
    p65 <= p90
  )
}

export const getBarBounds = (data: PriceRangeBarData): PriceRangeBarBounds => {
  const {p10, p90, hotelPrice} = data
  // Anchor an outer bound to `hotelPrice` ONLY when the price would
  // actually overflow the p10–p90 range. Inside that range we always show
  // the real percentile bounds — even when the pin sits in the LOW or
  // HIGH band — so the user can see the full distribution context (e.g.
  // when a hotel is just above p65 but well below p90, the right label
  // should stay at p90 instead of collapsing onto the hotel price and
  // hiding how high similar hotels can go).
  const leftBound = hotelPrice < p10 ? hotelPrice : p10
  const rightBound = hotelPrice > p90 ? hotelPrice : p90
  return {leftBound, rightBound}
}

/**
 * Per design: the indicator always snaps to the CENTER of the band that
 * contains the hotel price — never sits on a segment boundary or in a gap.
 *
 * - hotelPrice ≤ p35 → Low centre (16.67%)
 * - p35 < hotelPrice < p65 → Fair centre (50%)
 * - hotelPrice ≥ p65 → High centre (83.33%)
 */
export const getIndicatorPercent = (data: PriceRangeBarData): number => {
  const {p35, p65, hotelPrice} = data
  const lowCenter = BAND_START_LOW + BAND_WIDTH / 2
  const fairCenter = BAND_START_FAIR + BAND_WIDTH / 2
  const highCenter = BAND_START_HIGH + BAND_WIDTH / 2

  if (!Number.isFinite(hotelPrice)) return lowCenter
  if (hotelPrice <= p35) return lowCenter
  if (hotelPrice >= p65) return highCenter
  return fairCenter
}

export const BOUNDARY_POSITIONS = {
  leftBoundPct: 0,
  p35Pct: BAND_START_FAIR,
  p65Pct: BAND_START_HIGH,
  rightBoundPct: 100
} as const

export const SEGMENT_CENTERS = {
  lowPct: BAND_WIDTH / 2,
  fairPct: BAND_START_FAIR + BAND_WIDTH / 2,
  highPct: BAND_START_HIGH + BAND_WIDTH / 2
} as const
